/**
 * NetworkCirclesPage — 圈子列表 + 详情侧边面板
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Users, Globe, ChevronRight, RefreshCw, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  circleApi,
  type Circle,
  type CircleDetail,
  type CircleType,
  CIRCLE_TYPE_LABELS,
  CIRCLE_TYPE_COLORS,
} from '@/lib/network-api';
import { cn } from '@/lib/utils';

const TRADE_NETWORK_TYPES: CircleType[] = ['agent_network', 'consortium'];
const COLLAB_CIRCLE_TYPES: CircleType[] = ['vendor_panel', 'peer_circle'];

// ─── Circle Card ─────────────────────────────────────

function CircleCard({ circle, onClick }: { circle: Circle; onClick: () => void }) {
  const { t } = useTranslation();
  const typeColor = CIRCLE_TYPE_COLORS[circle.circle_type] ?? 'bg-gray-100 text-gray-600';
  const typeLabels: Record<CircleType, string> = {
    ownership: t('networkPage.circleTypes.ownership'),
    agent_network: t('networkPage.circleTypes.agent_network'),
    vendor_panel: t('networkPage.circleTypes.vendor_panel'),
    peer_circle: t('networkPage.circleTypes.peer_circle'),
    consortium: t('networkPage.circleTypes.consortium'),
  };
  const typeLabel = typeLabels[circle.circle_type] ?? CIRCLE_TYPE_LABELS[circle.circle_type] ?? circle.circle_type;

  return (
    <div
      className="group flex cursor-pointer flex-col gap-3 rounded-lg border border-border bg-card p-4 transition-all hover:border-primary/30 hover:shadow-md"
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <span className="font-semibold leading-tight text-foreground">{circle.name}</span>
          {circle.description && (
            <span className="line-clamp-2 text-xs text-muted-foreground">{circle.description}</span>
          )}
        </div>
        <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', typeColor)}>
          {typeLabel}
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Users className="size-3" />
          {t('networkPage.circles.memberCount', { count: circle.member_count })}
        </span>
        {circle.member_role === 'owner' && (
          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{t('networkPage.circles.owner')}</Badge>
        )}
        {circle.member_role === 'admin' && (
          <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{t('networkPage.circles.admin')}</Badge>
        )}
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────

function CircleDetailPanel({ circleId, onClose }: { circleId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<CircleDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    circleApi.get(circleId)
      .then(setDetail)
      .catch(() => toast.error(t('networkPage.circles.detailLoadFailed')))
      .finally(() => setLoading(false));
  }, [circleId, t]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (!detail) return null;

  const typeColor = CIRCLE_TYPE_COLORS[detail.circle_type] ?? '';
  const typeLabels: Record<CircleType, string> = {
    ownership: t('networkPage.circleTypes.ownership'),
    agent_network: t('networkPage.circleTypes.agent_network'),
    vendor_panel: t('networkPage.circleTypes.vendor_panel'),
    peer_circle: t('networkPage.circleTypes.peer_circle'),
    consortium: t('networkPage.circleTypes.consortium'),
  };
  const typeLabel = typeLabels[detail.circle_type] ?? CIRCLE_TYPE_LABELS[detail.circle_type] ?? detail.circle_type;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="flex flex-col gap-1">
          <h3 className="font-semibold text-foreground">{detail.name}</h3>
          <span className={cn('w-fit rounded-full px-2 py-0.5 text-xs font-medium', typeColor)}>
            {typeLabel}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>{t('networkPage.common.close')}</Button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {detail.description && (
          <p className="mb-4 text-sm text-muted-foreground">{detail.description}</p>
        )}

        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <Users className="size-4" /> {t('networkPage.circles.membersTitle', { count: detail.members.length })}
        </h4>
        <div className="flex flex-col gap-2">
          {detail.members.map(m => (
            <div key={m.id} className="flex items-center justify-between rounded-md border border-border p-3">
              <div className="flex items-center gap-2">
                {m.tenant_id ? (
                  <Building2 className="size-4 text-primary" />
                ) : (
                  <Globe className="size-4 text-muted-foreground" />
                )}
                <div>
                  <div className="text-sm font-medium">
                    {m.tenant_name ?? m.external_name ?? t('networkPage.common.unknown')}
                  </div>
                  {m.country_code && (
                    <div className="text-xs text-muted-foreground">{m.country_code}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {m.capabilities.slice(0, 2).map(cap => (
                  <Badge key={cap} variant="secondary" className="text-[10px]">{cap}</Badge>
                ))}
                <Badge variant="outline" className="text-[10px]">{m.member_role}</Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Create Dialog ─────────────────────────────────────

function CreateCircleDialog({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (c: Circle) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [type, setType] = useState<CircleType>('agent_network');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error(t('networkPage.circles.nameRequired')); return; }
    setSaving(true);
    try {
      const circle = await circleApi.create({ name: name.trim(), circle_type: type, description: description.trim() || undefined });
      toast.success(t('networkPage.circles.createdSuccess'));
      onCreated(circle);
      setName(''); setDescription('');
    } catch (e: unknown) {
      toast.error((e as Error).message ?? t('networkPage.circles.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const typeLabels: Record<CircleType, string> = {
    ownership: t('networkPage.circleTypes.ownership'),
    agent_network: t('networkPage.circleTypes.agent_network'),
    vendor_panel: t('networkPage.circleTypes.vendor_panel'),
    peer_circle: t('networkPage.circleTypes.peer_circle'),
    consortium: t('networkPage.circleTypes.consortium'),
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{t('networkPage.circles.createTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="circle-name">{t('networkPage.circles.nameLabel')}</Label>
            <Input id="circle-name" placeholder={t('networkPage.circles.namePlaceholder')} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="circle-type">{t('networkPage.circles.typeLabel')}</Label>
            <Select value={type} onValueChange={v => setType(v as CircleType)}>
              <SelectTrigger id="circle-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {([...TRADE_NETWORK_TYPES, ...COLLAB_CIRCLE_TYPES] as CircleType[]).map((k) => (
                  <SelectItem key={k} value={k}>{typeLabels[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="circle-desc">{t('networkPage.circles.descLabel')}</Label>
            <Textarea id="circle-desc" placeholder={t('networkPage.circles.descPlaceholder')} rows={3} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('networkPage.common.cancel')}</Button>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? t('networkPage.circles.createSubmitting') : t('networkPage.circles.createAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Page ─────────────────────────────────────────

export default function NetworkCirclesPage() {
  const { t } = useTranslation();
  const [circles, setCircles] = useState<Circle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    circleApi.list()
      .then(setCircles)
      .catch(() => toast.error(t('networkPage.circles.listLoadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  const typeLabels: Record<CircleType, string> = {
    ownership: t('networkPage.circleTypes.ownership'),
    agent_network: t('networkPage.circleTypes.agent_network'),
    vendor_panel: t('networkPage.circleTypes.vendor_panel'),
    peer_circle: t('networkPage.circleTypes.peer_circle'),
    consortium: t('networkPage.circleTypes.consortium'),
  };

  useEffect(() => { load(); }, [load]);

  const groupedByType = circles.reduce<Record<string, Circle[]>>((acc, c) => {
    const k = c.circle_type;
    if (!acc[k]) acc[k] = [];
    acc[k].push(c);
    return acc;
  }, {});
  const tradeNetworks = TRADE_NETWORK_TYPES.flatMap(type => groupedByType[type] ?? []);
  const collaborationCircles = COLLAB_CIRCLE_TYPES.flatMap(type => groupedByType[type] ?? []);

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main list */}
      <div className={cn('flex flex-1 flex-col overflow-hidden', selectedId ? 'border-r border-border' : '')}>
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h1 className="text-lg font-semibold">{t('networkPage.circles.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('networkPage.circles.subtitle')}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="h-5 px-1.5 text-[10px] uppercase tracking-wide">
                {t('networkPage.circles.ownershipNoteBadge')}
              </Badge>
              <span>{t('networkPage.circles.ownershipNoteDesc')}</span>
              <Link to="/network/group" className="font-medium text-primary underline-offset-4 hover:underline">
                {t('networkPage.circles.openGroupPage')}
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="size-4" />
              {t('networkPage.circles.createAction')}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-28 rounded-lg" />
              ))}
            </div>
          ) : circles.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-20 text-center">
              <Users className="size-12 text-muted-foreground/40" />
              <div>
                <p className="font-medium text-muted-foreground">{t('networkPage.circles.emptyTitle')}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t('networkPage.circles.emptyDesc')}</p>
              </div>
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="size-4" /> {t('networkPage.circles.createAction')}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              <section>
                <div className="mb-3 flex items-end justify-between gap-4">
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('networkPage.circles.tradeNetworksTitle')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">{t('networkPage.circles.tradeNetworksDesc')}</p>
                  </div>
                  <Badge variant="outline">{t('networkPage.circles.typeCount', { count: tradeNetworks.length })}</Badge>
                </div>
                {tradeNetworks.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                    {t('networkPage.circles.tradeNetworksEmptyDesc')}
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {TRADE_NETWORK_TYPES.flatMap(circleType => (groupedByType[circleType] ?? []).map(c => (
                      <CircleCard
                        key={c.id}
                        circle={c}
                        onClick={() => setSelectedId(s => s === c.id ? null : c.id)}
                      />
                    )))}
                  </div>
                )}
              </section>

              <section>
                <div className="mb-3 flex items-end justify-between gap-4">
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      {t('networkPage.circles.circlesSectionTitle')}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">{t('networkPage.circles.circlesSectionDesc')}</p>
                  </div>
                  <Badge variant="outline">{t('networkPage.circles.typeCount', { count: collaborationCircles.length })}</Badge>
                </div>
                {collaborationCircles.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
                    {t('networkPage.circles.circlesSectionEmptyDesc')}
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {COLLAB_CIRCLE_TYPES.flatMap(circleType => (groupedByType[circleType] ?? []).map(c => (
                      <CircleCard
                        key={c.id}
                        circle={c}
                        onClick={() => setSelectedId(s => s === c.id ? null : c.id)}
                      />
                    )))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {selectedId && (
        <div className="w-[400px] shrink-0 overflow-hidden">
          <CircleDetailPanel
            circleId={selectedId}
            onClose={() => setSelectedId(null)}
          />
        </div>
      )}

      {/* Create dialog */}
      <CreateCircleDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={c => {
          setCircles(prev => [c, ...prev]);
          setShowCreate(false);
          setSelectedId(c.id);
        }}
      />
    </div>
  );
}
